import React, { useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { getSinceInstallationLabel } from "../../utils/installationDate";
import { useV2Year } from "../../hooks/useV2Year";
import "./Page.css";
import "./ActionsWater.css";
import "./Dashboard.css";
import { GraphCard, formatSummaryStatDelta } from "../../components/v2/cards";
import { DashboardSummaryCardsRow, EconomiesTrendCard } from "./DashboardCards";
import ActionsWaterSavingsExplanation from "./ActionsWaterSavingsExplanation";
import {
  buildEconomiesCalendarSeries,
  buildShowerConsumptionCalendarSeries,
  formatDashboardChartData,
} from "../../utils/formatDashboardChartData";
import {
  getDemoHotel73DashboardStats,
  getDemoHotel73PollutionStats,
  getDemoHotel73WaterStats,
  isDemoHotel73,
} from "../../utils/demoHotel73Stats";

const getShowerConsumptionLabel = (dataKey, lang) => {
  if (dataKey === "consoTemoin") return translate("v2_actions_water_legend_without", lang);
  if (dataKey === "consoLunishower") return translate("v2_actions_water_legend_with", lang);
  return dataKey;
};

const getEconomiesSavingsLabel = (dataKey, lang) => {
  if (dataKey === "waterSavings" || dataKey === "waterSavingsChart") {
    return translate("v2_actions_water_tooltip_water", lang);
  }
  if (dataKey === "energySavings") return translate("v2_actions_water_tooltip_energy", lang);
  return dataKey;
};

const PENDING_BAR_FILL = "#DBDBDB";
const PENDING_BAR_HEIGHT = 48;
const PENDING_LABEL_WIDTH = 72;
const PENDING_LABEL_PADDING_Y = 5;
const PENDING_LABEL_LINE_HEIGHT = 9;
const PENDING_LABEL_FONT_SIZE = 8;

const getPendingMessageLines = (lang) => [
  translate("v2_pending_data_line1", lang),
  translate("v2_pending_data_line2", lang),
  translate("v2_pending_data_line3", lang),
];

const formatBarTopLabel = (value, locale) => {
  const n = Number(value) || 0;
  if (n <= 0) return null;
  return n.toLocaleString(locale, {
    maximumFractionDigits: n >= 100 ? 0 : 1,
  });
};

const BarTopLabel = ({ x, y, width, value, fill, locale }) => {
  const label = formatBarTopLabel(value, locale);
  if (!label) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fill={fill}
      fontFamily="Montserrat, sans-serif"
      fontSize={14}
      fontWeight={500}
    >
      {label}
    </text>
  );
};

const SHOWER_BAR_SIZE = 20;
const SHOWER_BAR_GAP = 8;

const isPlatformTogglePending = (payload) => {
  const hasMonthlyRecord = Boolean(payload?.hasMonthlyRecord);
  const showOnPlatform = Number(payload?.showOnPlatform) || 0;
  return hasMonthlyRecord && showOnPlatform !== 1;
};

const ShowerConsumptionTooltip = ({ active, payload }) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const point = payload?.[0]?.payload;
  if (
    !active ||
    !payload?.length ||
    point?.isFutureMonth ||
    isPlatformTogglePending(point)
  ) {
    return null;
  }

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(27, 36, 27, 0.15)",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 15,
        color: "#1e3529",
      }}
    >
      {payload.map((entry) => {
        const label = getShowerConsumptionLabel(entry.dataKey, lang) || entry.name;
        const value = Number(entry.value) || 0;
        return (
          <div key={entry.dataKey}>
            {`${label} : ${value.toLocaleString(locale, { maximumFractionDigits: 2 })}`}
          </div>
        );
      })}
    </div>
  );
};

const ShowerConsumptionTemoinBar = (props) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const { x, y, width, height, value, payload, background } = props;
  if (payload?.isFutureMonth) {
    return null;
  }
  if (isPlatformTogglePending(payload)) {
    return <PendingDualBarGroup x={x} y={y} height={height} background={background} lang={lang} />;
  }

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill="#D1E4FF" rx={2} />
      <BarTopLabel x={x} y={y} width={width} value={value} fill="#5B97EE" locale={locale} />
    </g>
  );
};

const ShowerConsumptionLunishowerBar = (props) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const { payload } = props;
  if (payload?.isFutureMonth || isPlatformTogglePending(payload)) {
    return null;
  }

  const { x, y, width, height, value } = props;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill="#5B97EE" rx={2} />
      <BarTopLabel x={x} y={y} width={width} value={value} fill="#5B97EE" locale={locale} />
    </g>
  );
};

const PendingDualBarGroup = ({ x, y, height, background, lang }) => {
  const pendingMessageLines = getPendingMessageLines(lang);
  const chartBottom = background ? background.y + background.height : y + height;
  const pendingY = chartBottom - PENDING_BAR_HEIGHT;
  const labelBlockHeight =
    PENDING_LABEL_PADDING_Y * 2 + pendingMessageLines.length * PENDING_LABEL_LINE_HEIGHT;
  const labelTop = pendingY - labelBlockHeight - 4;
  const groupWidth = SHOWER_BAR_SIZE * 2 + SHOWER_BAR_GAP;
  const centerX = x + groupWidth / 2;
  const labelLeft = centerX - PENDING_LABEL_WIDTH / 2;

  return (
    <g>
      <rect
        x={labelLeft}
        y={labelTop}
        width={PENDING_LABEL_WIDTH}
        height={labelBlockHeight}
        fill="#F3F3F3"
        rx={5}
      />
      {pendingMessageLines.map((line, index) => (
        <text
          key={line}
          x={centerX}
          y={labelTop + PENDING_LABEL_PADDING_Y + PENDING_LABEL_FONT_SIZE + index * PENDING_LABEL_LINE_HEIGHT}
          textAnchor="middle"
          fill="#393939"
          fontFamily="Montserrat, sans-serif"
          fontSize={PENDING_LABEL_FONT_SIZE}
          fontStyle="italic"
          fontWeight={400}
        >
          {line}
        </text>
      ))}
      <rect x={x} y={pendingY} width={SHOWER_BAR_SIZE} height={PENDING_BAR_HEIGHT} fill={PENDING_BAR_FILL} rx={2} />
      <rect
        x={x + SHOWER_BAR_SIZE + SHOWER_BAR_GAP}
        y={pendingY}
        width={SHOWER_BAR_SIZE}
        height={PENDING_BAR_HEIGHT}
        fill={PENDING_BAR_FILL}
        rx={2}
      />
    </g>
  );
};

const EconomiesSavingsTooltip = ({ active, payload }) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const point = payload?.[0]?.payload;
  if (
    !active ||
    !payload?.length ||
    point?.isFutureMonth ||
    isPlatformTogglePending(point)
  ) {
    return null;
  }

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(27, 36, 27, 0.15)",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 15,
        color: "#1e3529",
      }}
    >
      {payload.map((entry) => {
        const label = getEconomiesSavingsLabel(entry.dataKey, lang) || entry.name;
        const isWater = entry.dataKey === "waterSavings" || entry.dataKey === "waterSavingsChart";
        const value = isWater
          ? Number(entry.payload?.waterSavings) || 0
          : Number(entry.value) || 0;
        const unit = isWater ? "m³" : "kWh";
        return (
          <div key={entry.dataKey}>
            {`${label} : ${value.toLocaleString(locale, { maximumFractionDigits: 2 })} ${unit}`}
          </div>
        );
      })}
    </div>
  );
};

const EconomiesWaterBar = (props) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const { x, y, width, height, value, payload, background } = props;
  if (payload?.isFutureMonth) {
    return null;
  }
  if (isPlatformTogglePending(payload)) {
    return <PendingDualBarGroup x={x} y={y} height={height} background={background} lang={lang} />;
  }

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill="#5B97EE" rx={2} />
      <BarTopLabel
        x={x}
        y={y}
        width={width}
        value={payload?.waterSavings ?? value}
        fill="#5B97EE"
        locale={locale}
      />
    </g>
  );
};

const EconomiesEnergyBar = (props) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  if (props.payload?.isFutureMonth || isPlatformTogglePending(props.payload)) {
    return null;
  }

  const { x, y, width, height, value } = props;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill="#C68A6A" rx={2} />
      <BarTopLabel x={x} y={y} width={width} value={value} fill="#C68A6A" locale={locale} />
    </g>
  );
};

const ActionsWater = () => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);

  const { year: selectedYear } = useV2Year();
  const [showerChartYear, setShowerChartYear] = useState(() => selectedYear);
  const [economiesChartYear, setEconomiesChartYear] = useState(() => selectedYear);
  const [financialChartYear, setFinancialChartYear] = useState(() => selectedYear);
  const [platformInstallationMissing, setPlatformInstallationMissing] = useState(false);
  const [installationDate, setInstallationDate] = useState(null);

  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [waterStats, setWaterStats] = useState({
    waterSaved: { value: 0, variation: 0 },
    moneySaved: { value: 0, variation: 0, currency: "EUR" },
    waterSavedByProduct: { total: 0, details: [] },
    showerConsumption: [],
    monthlySavings: [],
  });

  const [pollutionStats, setPollutionStats] = useState({
    energySaved: { value: 0, variation: 0, unit: "kWh" },
    co2Saved: { value: 0, variation: 0, unit: "kg" },
  });
  const [financialChartData, setFinancialChartData] = useState([]);
  const [financialChartDataRaw, setFinancialChartDataRaw] = useState([]);
  const [financialNoDataAvailable, setFinancialNoDataAvailable] = useState(false);

  const fetchStats = () => {
    if (!keycloak || !keycloak.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setHasError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    if (isDemoHotel73(hId)) {
      const waterData = getDemoHotel73WaterStats();
      const pollutionData = getDemoHotel73PollutionStats();
      const statsData = getDemoHotel73DashboardStats();
      setWaterStats(waterData);
      setPollutionStats(pollutionData);
      setPlatformInstallationMissing(false);
      setInstallationDate(waterData.installationDate || null);
      setFinancialChartDataRaw(statsData.chartData || []);
      setFinancialNoDataAvailable(false);
      setHasError(false);
      setLoading(false);
      return;
    }

    const params = new URLSearchParams({ lang });

    const commonOptions = {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };

    const waterUrl = `/api/hotels/${hId}/water/stats?${params.toString()}`;
    const pollutionUrl = `/api/hotels/${hId}/pollution/stats?${params.toString()}`;
    const statsUrl = `/api/hotels/${hId}/stats?periodType=beginning`;

    Promise.all([
      fetch(waterUrl, commonOptions),
      fetch(pollutionUrl, commonOptions),
      fetch(statsUrl, {
        ...commonOptions,
        headers: {
          Authorization: commonOptions.headers.Authorization,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    ])
      .then(async ([waterResp, pollutionResp, statsResp]) => {
        if (!waterResp.ok) {
          const errData = await waterResp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP error (water): ${waterResp.status}`);
        }
        if (!pollutionResp.ok) {
          const errData = await pollutionResp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP error (pollution): ${pollutionResp.status}`);
        }

        const waterData = await waterResp.json();
        const pollutionData = await pollutionResp.json();
        const statsData = await statsResp.json().catch(() => ({}));

        setWaterStats(waterData);
        setPollutionStats(pollutionData);
        const installationMissing =
          Boolean(waterData.platformInstallationMissing) ||
          Boolean(statsData.platformInstallationMissing);
        setPlatformInstallationMissing(installationMissing);
        setInstallationDate(waterData.installationDate || statsData.installationDate || null);

        if (statsData.success === "OK") {
          setFinancialChartDataRaw(statsData.chartData || []);
          setFinancialNoDataAvailable(false);
        } else if (statsData.success === "FAIL" && statsData.message === "No data found for graph.") {
          setFinancialChartDataRaw([]);
          setFinancialNoDataAvailable(true);
        } else if (!statsResp.ok) {
          setFinancialChartDataRaw([]);
          setFinancialNoDataAvailable(false);
        }

        setHasError(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("ActionsWater - Error fetching stats:", error);
        setHasError(true);
        setInstallationDate(null);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (keycloak && keycloak.isAuthenticated && keycloak.user && hId) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.isAuthenticated, hId]);

  useEffect(() => {
    setShowerChartYear(selectedYear);
    setEconomiesChartYear(selectedYear);
    setFinancialChartYear(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    setFinancialChartData(
      formatDashboardChartData(financialChartDataRaw, financialChartYear, null, lang, new Date(), {
        platformInstallationMissing,
      })
    );
  }, [financialChartDataRaw, financialChartYear, lang, platformInstallationMissing]);

  const chartOptions = useMemo(
    () => ({ platformInstallationMissing }),
    [platformInstallationMissing]
  );

  const cardWater = waterStats?.waterSaved || { value: 0, variation: 0 };
  const cardEnergy = pollutionStats?.energySaved || { value: 0, variation: 0 };
  const summarySubtitle = getSinceInstallationLabel(installationDate, lang, locale);

  const summaryCards = useMemo(
    () => [
      {
        id: "water",
        title: translate("v2_actions_water_card_water", lang),
        subtitle: summarySubtitle,
        value: Math.round(cardWater.value || 0).toLocaleString(locale),
        valueUnit: "L",
        deltaValue: formatSummaryStatDelta(cardWater.variation, " L", locale),
        accentColor: "#5B97EE",
        loading,
      },
      {
        id: "energy",
        title: translate("v2_actions_water_card_energy", lang),
        subtitle: summarySubtitle,
        value: Math.round(cardEnergy.value || 0).toLocaleString(locale),
        valueUnit: "kWh",
        deltaValue: formatSummaryStatDelta(cardEnergy.variation, " kWh", locale),
        accentColor: "#C68A6A",
        loading,
      },
    ],
    [cardWater.value, cardWater.variation, cardEnergy.value, cardEnergy.variation, loading, summarySubtitle, lang, locale]
  );

  const showerSeries = useMemo(
    () =>
      buildShowerConsumptionCalendarSeries(
        waterStats?.showerConsumption,
        showerChartYear,
        locale,
        new Date(),
        chartOptions
      ),
    [waterStats?.showerConsumption, showerChartYear, locale, chartOptions]
  );

  const economiesSeries = useMemo(() => {
    const series = buildEconomiesCalendarSeries(
      waterStats?.monthlySavings,
      economiesChartYear,
      locale,
      new Date(),
      chartOptions
    );
    const maxWater = Math.max(...series.map((point) => Number(point.waterSavings) || 0), 0);
    const maxEnergy = Math.max(...series.map((point) => Number(point.energySavings) || 0), 0);
    const waterBarVisualScale = maxWater > 0 && maxEnergy > 0 ? maxEnergy / maxWater : 1;

    return series.map((point) => ({
      ...point,
      waterSavingsChart: (Number(point.waterSavings) || 0) * waterBarVisualScale,
    }));
  }, [waterStats?.monthlySavings, economiesChartYear, locale, chartOptions]);

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_actions_water_title", lang)}</h1>
      </div>
      {hasError && (
        <div style={{ padding: "20px", textAlign: "center", color: "#d32f2f" }}>
          <p>{translate("v2_error_load_data", lang)}</p>
        </div>
      )}
      <div className="actions-water__content">
        <DashboardSummaryCardsRow cards={summaryCards} />

        <GraphCard
          className="actions-water__card actions-water__card-large"
          title={translate("v2_actions_water_shower_title", lang)}
          subtitle={translate("v2_actions_water_shower_subtitle", lang)}
          showYearSelector={true}
          year={showerChartYear}
          onPreviousYear={() => setShowerChartYear((prev) => prev - 1)}
          onNextYear={() => setShowerChartYear((prev) => prev + 1)}
          legendContent={
            <div className="actions-water__dual-legend">
              <div className="actions-water__dual-legend-item">
                <span className="actions-water__dual-legend-dot actions-water__dual-legend-dot--light-blue" />
                <span>{translate("v2_actions_water_legend_without", lang)}</span>
              </div>
              <div className="actions-water__dual-legend-item">
                <span className="actions-water__dual-legend-dot actions-water__dual-legend-dot--blue" />
                <span>{translate("v2_actions_water_legend_with", lang)}</span>
              </div>
            </div>
          }
        >
          <div className="actions-water__graph-card-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={showerSeries} barGap={SHOWER_BAR_GAP} margin={{ top: 58, right: 10, left: 10, bottom: 10 }}>
                <XAxis dataKey="monthLabel" tick={{ fontSize: 14 }} interval={0} />
                <YAxis hide />
                <Tooltip content={<ShowerConsumptionTooltip />} />
                <Bar
                  dataKey="consoTemoin"
                  name="consoTemoin"
                  fill="#D1E4FF"
                  isAnimationActive={false}
                  barSize={SHOWER_BAR_SIZE}
                  shape={(props) => <ShowerConsumptionTemoinBar {...props} />}
                />
                <Bar
                  dataKey="consoLunishower"
                  name="consoLunishower"
                  fill="#5B97EE"
                  isAnimationActive={false}
                  barSize={SHOWER_BAR_SIZE}
                  shape={(props) => <ShowerConsumptionLunishowerBar {...props} />}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GraphCard>

        <GraphCard
          className="actions-water__card actions-water__card-large"
          title={translate("v2_actions_water_evolution_title", lang)}
          subtitle={translate("v2_actions_water_evolution_subtitle", lang)}
          showYearSelector={true}
          year={economiesChartYear}
          onPreviousYear={() => setEconomiesChartYear((prev) => prev - 1)}
          onNextYear={() => setEconomiesChartYear((prev) => prev + 1)}
          legendContent={
            <div className="actions-water__dual-legend">
              <div className="actions-water__dual-legend-item">
                <span className="actions-water__dual-legend-dot actions-water__dual-legend-dot--blue" />
                <span>{translate("v2_actions_water_legend_water_m3", lang)}</span>
              </div>
              <div className="actions-water__dual-legend-item">
                <span className="actions-water__dual-legend-dot actions-water__dual-legend-dot--brown" />
                <span>{translate("v2_actions_water_legend_energy_kwh", lang)}</span>
              </div>
            </div>
          }
        >
          <div className="actions-water__graph-card-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={economiesSeries} barGap={SHOWER_BAR_GAP} margin={{ top: 58, right: 10, left: 10, bottom: 10 }}>
                <XAxis dataKey="monthLabel" tick={{ fontSize: 14 }} interval={0} />
                <YAxis hide />
                <Tooltip content={<EconomiesSavingsTooltip />} />
                <Bar
                  dataKey="waterSavingsChart"
                  name="waterSavings"
                  fill="#5B97EE"
                  isAnimationActive={false}
                  barSize={SHOWER_BAR_SIZE}
                  shape={(props) => <EconomiesWaterBar {...props} />}
                />
                <Bar
                  dataKey="energySavings"
                  name="energySavings"
                  fill="#C68A6A"
                  isAnimationActive={false}
                  barSize={SHOWER_BAR_SIZE}
                  shape={(props) => <EconomiesEnergyBar {...props} />}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GraphCard>

        <EconomiesTrendCard
          loading={loading}
          hasError={hasError}
          noDataAvailable={financialNoDataAvailable}
          chartData={financialChartData}
          selectedYear={financialChartYear}
          luniShowerOnly={true}
          onPreviousYear={() => setFinancialChartYear((prev) => prev - 1)}
          onNextYear={() => setFinancialChartYear((prev) => prev + 1)}
        />

        <ActionsWaterSavingsExplanation />
      </div>
    </div>
  );
};

export default ActionsWater;
